import { SettingNavigationProvider } from "@redotech/redo-merchant-app-common/global-search/setting-search/setting-navigator-context";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { SettingsSectionEnum } from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { Navigate } from "react-router-dom";
import {
  aiSettingsRoutes,
  inAiKnowledgeView,
} from "../../../setting/ai-agent/route";
import { conversionRoutes } from "../../../setting/conversion/route";
import { inFaqBuilder, inFaqHomeView } from "./faq-routes";
import { chargebackSettingsRoutes } from "./settings-routes/chargeback";
import { customerAccountSettingsRoutes } from "./settings-routes/customer-account";
import { fulfillmentSettingsRoutes } from "./settings-routes/fulfillment";
import { generalSettingsRoutes } from "./settings-routes/general";
import { internationalSettingsRoutes } from "./settings-routes/international";
import { inventorySettingsRoutes } from "./settings-routes/inventory";
import { marketingSettingsRoutes } from "./settings-routes/marketing";
import { orderTrackingSettingsRoutes } from "./settings-routes/order-tracking";
import { productSettingsRoutes } from "./settings-routes/product";
import { returnSettingsRoutes } from "./settings-routes/return";
import { shippingSettingsRoutes } from "./settings-routes/shipping";
import { suppliesSettingsRoutes } from "./settings-routes/supplies";
import { supportSettingsRoutes } from "./settings-routes/support";
import { warrantySettingsRoutes } from "./settings-routes/warranty";

const fullscreen = (url: string) => {
  return url.includes("ad-blocks/") && !url.endsWith("ad-blocks/");
};

const inNewFlowEditor = (url: string) => {
  return (
    url.includes("/flows/returns") ||
    url.includes("/flows/claims") ||
    url.includes("/flows/warranties") ||
    url.includes("/flows/withdrawals") ||
    inCompensationFlowEditor(url) ||
    inRestockingFlowEditor(url)
  );
};

const inCompensationFlowEditor = (url: string) => {
  return url.includes("compensation-flow-editor");
};

const inRestockingFlowEditor = (url: string) => {
  return url.includes("restocking-flow-editor");
};

const inLocationEditor = (url: string) => {
  return url.includes("location-flow-editor");
};

const inLabelsEditor = (url: string) => {
  return url.includes("labels-flow-editor");
};

const inForge = (url: string) => {
  return (
    (url.includes("reviews-displays-widgets/") &&
      !url.endsWith("reviews-displays-widgets/")) ||
    (url.includes("upsell-page/") && !url.endsWith("upsell-page/")) ||
    (url.includes("oms/automations") && !url.endsWith("oms/automations")) ||
    inFaqBuilder(url) ||
    url.includes("oms/warehouse-builder") ||
    url.includes("conversion/general/checkout-widgets-builder") ||
    inWorkOrderFlowBuilder(url) ||
    inSuppliesShippingBuilder(url)
  );
};

const inPhoneSetup = (url: string) => {
  return url.includes("phone-setup");
};

const inBilling = (url: string) => {
  return url.includes("/settings/billing");
};

const inAutomationsList = (url: string) => {
  return url.endsWith("oms/automations");
};

const inWorkOrderFlowsList = (url: string) => {
  return url.endsWith("oms/work-order-flows");
};

const inWorkOrderFlowBuilder = (url: string) => {
  return url.includes("oms/work-order-flows/");
};

const inSuppliesShippingBuilder = (url: string) => {
  return url.includes("supplies/shipping-rules");
};

const inProcessingSettings = (url: string) => {
  return (
    url.endsWith("settings/automations/processing") ||
    url.endsWith("settings/automations/processing/")
  );
};

const inReturnProcessingFlowBuilder = (url: string) => {
  return (
    url.includes("settings/automations/") &&
    !inProcessingSettings(url) &&
    !inCompensationFlowEditor(url) &&
    !inRestockingFlowEditor(url)
  );
};

const inPortalBuilder = (url: string) => {
  return url.includes("settings/portal-builder");
};

const inCoverageBuilder = (url: string) => {
  return url.includes("coverage-builder");
};

const inCoverageSettings = (url: string) => {
  return url.includes("settings/coverage") && !inCoverageBuilder(url);
};

const inShippingSettings = (url: string) => {
  return (
    url.includes("settings/shipping") &&
    !inLocationEditor(url) &&
    !inLabelsEditor(url)
  );
};

const inPackingSlipBuilder = (url: string) => {
  return url.includes("settings/oms/printing/packing-slips/");
};

const isWarehouseLayout = (url: string) => {
  return url.endsWith("oms/warehouse-layouts");
};

const isFullwidthTable = (url: string) => {
  return (
    url.includes("settings/attributes") ||
    url.includes("settings/exchanges/guidance")
  );
};

const inRuleEditor = (url: string) => {
  return (
    url.includes("settings/rules/edit-v3/") ||
    url.includes("settings/rules/edit-default/")
  );
};

function hidePadding(url: string) {
  return (
    inAiKnowledgeView(url) ||
    inAutomationsList(url) ||
    inWorkOrderFlowsList(url) ||
    fullscreen(url) ||
    inForge(url) ||
    inReturnProcessingFlowBuilder(url) ||
    inPhoneSetup(url) ||
    inBilling(url) ||
    isWarehouseLayout(url) ||
    inFaqHomeView(url) ||
    isFullwidthTable(url) ||
    (url.includes("customer-portal") && !inNewFlowEditor(url)) ||
    inPortalBuilder(url) ||
    inCoverageBuilder(url) ||
    inCoverageSettings(url) ||
    inShippingSettings(url) ||
    inPackingSlipBuilder(url) ||
    inRuleEditor(url)
  );
}

const settingsRoutesBySection: Record<
  SettingsSectionEnum,
  PermissionedRouteObject[] | PermissionedRouteObject
> = {
  [SettingsSectionEnum.GENERAL]: generalSettingsRoutes,
  [SettingsSectionEnum.AI]: aiSettingsRoutes,
  [SettingsSectionEnum.CHARGEBACKS]: chargebackSettingsRoutes,
  [SettingsSectionEnum.CONVERSION]: conversionRoutes,
  [SettingsSectionEnum.CUSTOMER_ACCOUNTS]: customerAccountSettingsRoutes,
  [SettingsSectionEnum.INTERNATIONAL]: internationalSettingsRoutes,
  [SettingsSectionEnum.INVENTORY_MANAGEMENT]: inventorySettingsRoutes,
  [SettingsSectionEnum.MARKETING]: marketingSettingsRoutes,
  [SettingsSectionEnum.ORDER_FULFILLMENT]: fulfillmentSettingsRoutes,
  [SettingsSectionEnum.ORDER_TRACKING]: orderTrackingSettingsRoutes,
  [SettingsSectionEnum.PRODUCTS]: productSettingsRoutes,
  [SettingsSectionEnum.RECOVER]: [],
  [SettingsSectionEnum.RETURNS_AND_CLAIMS]: returnSettingsRoutes,
  [SettingsSectionEnum.SHIPPING]: shippingSettingsRoutes,
  [SettingsSectionEnum.SUPPLIES]: suppliesSettingsRoutes,
  [SettingsSectionEnum.SUPPORT]: supportSettingsRoutes,
  [SettingsSectionEnum.WARRANTIES]: warrantySettingsRoutes,
  [SettingsSectionEnum.PERSONAL]: [
    {
      handle: { breadcrumb: "Personal" },
      path: "personal",
      children: [
        { index: true, element: <Navigate replace to="profile" /> },
        {
          handle: { breadcrumb: "Profile settings" },
          path: "profile",
          lazy: async () => {
            const { ProfilePage } =
              await import("@redotech/redo-merchant-general-app/setting/user/profile-page");
            return { Component: ProfilePage };
          },
        },
      ],
    },
  ],
};

const routeChildren = Object.values(settingsRoutesBySection).reduce<
  PermissionedRouteObject[]
>((acc, section) => {
  if (Array.isArray(section)) {
    return [...acc, ...section];
  } else {
    return [...acc, section];
  }
}, [] as PermissionedRouteObject[]);

// Add redirects for old routes
routeChildren.push(
  // Redirect old try-first routes to conversion/try-first
  {
    path: "try-first",
    element: <Navigate replace to="../conversion/try-first" />,
  },
  {
    path: "try-first/*",
    element: <Navigate replace to="../conversion/try-first" />,
  },

  // Redirect old checkout-optimization routes to conversion/delivery-estimate
  {
    path: "checkout-optimization",
    element: <Navigate replace to="../conversion/delivery-estimate" />,
  },
  {
    path: "checkout-optimization/*",
    element: <Navigate replace to="../conversion/delivery-estimate" />,
  },
);

export const settingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Settings", breadcrumbNavigate: false },
    lazy: async () => {
      const { Page } = await import("../../page");
      return {
        element: (
          <SettingNavigationProvider>
            <Page
              hideHeader={(url) =>
                fullscreen(url) ||
                inForge(url) ||
                inPhoneSetup(url) ||
                inNewFlowEditor(url) ||
                inLocationEditor(url) ||
                inLabelsEditor(url) ||
                inReturnProcessingFlowBuilder(url) ||
                inPortalBuilder(url) ||
                inCoverageBuilder(url)
              }
              hideNavbar={(url) =>
                fullscreen(url) ||
                inForge(url) ||
                inPhoneSetup(url) ||
                inNewFlowEditor(url) ||
                inLocationEditor(url) ||
                inLabelsEditor(url) ||
                inReturnProcessingFlowBuilder(url) ||
                inPortalBuilder(url) ||
                inCoverageBuilder(url)
              }
              hidePadding={hidePadding}
            />
          </SettingNavigationProvider>
        ),
      };
    },
    children: routeChildren,
  },
];
